import { default as Router } from 'next/router';
import {
  maxPriceUrlPrefix,
  minPriceUrlPrefix,
} from '../../helpers/domain/priceOptions';
import {
  getAllPropertyTypeValues,
  PropertyType,
} from '../domain/propertyTypes';
import commonResource from '../../../locales/th/common.json';

/**
 * Query Parameters of the SRP page.
 */
export type SrpQueryParams = Partial<{
  type: string;
  parent: string;
  slug: string;
  q: string;
  minPrice: string;
  propertyType: PropertyType;
  maxPrice: string;
}>;

/**
 * Get the relative url for the search-result-page
 */
export function getSrpRouteUrl(queryParams: SrpQueryParams): string {
  const { minPrice, maxPrice, propertyType, parent, q, slug, type } =
    queryParams;
  const priceSegment = getPriceSegment(minPrice, maxPrice);
  const segments = [
    type,
    parent,
    slug,
    propertyType && getAllPropertyTypeValues(propertyType),
    priceSegment,
  ]
    .filter((segment) => segment)
    .map((segment) => encodeURIComponent(segment));
  const queryUrl = getQueryParamUrl({ q });
  return `/list/${segments.join('/')}${segments.length ? '/' : ''}${queryUrl}`;
}

export function gotoSrp(queryParams: SrpQueryParams): void {
  const url = getSrpRouteUrl(queryParams);
  Router.push(url);
}

function getPriceSegment(minPriceParam: string, maxPriceParam: string): string {
  let result: string;
  if (minPriceParam && maxPriceParam) {
    result = `${minPriceParam}-${maxPriceParam}`;
  } else if (minPriceParam) {
    result = `${minPriceUrlPrefix}-${minPriceParam}`;
  } else if (maxPriceParam) {
    result = `${maxPriceUrlPrefix}-${maxPriceParam}`;
  }

  return result;
}

function getQueryParamUrl({
  q,
  minPrice,
  maxPrice,
}: {
  q?: string;
  minPrice?: string;
  maxPrice?: string;
}): string {
  const searchParams = new URLSearchParams();
  if (q) {
    searchParams.append('q', q);
  }

  if (minPrice) {
    searchParams.append('minPrice', minPrice);
  }

  if (maxPrice) {
    searchParams.append('maxPrice', maxPrice);
  }

  const url = searchParams.toString();
  return url.length ? `?${url}` : '';
}

/**
 * Check if the SRP page url is used as the commercial building landing page.
 * Only the url with the specified keyword `q` and NO other parameters are considered as the landing page.
 * @param params - SRP query params
 */
export function isCommercialBuildingLandingPage(params: SrpQueryParams) {
  const { q, ...rest } = params;
  return (
    q === commonResource.propertyTypes.commercial &&
    Object.values(rest).every((value) => !value)
  );
}
